import type { QueryRequestData } from '@ricoh/ria.client.core/plugins/swr/request';
import type { UnknownArray } from '@ricoh/ria.client.core/types/core/data.type';
import type { AxiosResponse } from 'axios';
import type { CamelCase } from 'type-fest';

import { orderBy } from '@progress/kendo-data-query';
import { ClientError } from '@ricoh/ria.client.core/errors';
import { camelCase } from 'change-case';

import type {
  NextableData,
  PageableData,
  PageableDataWithTrailing,
  ServerResponse,
  ServerResponsePageable,
  ServerResponsePageableWithTrailing,
} from './_data.type';

/**
 * GetHeaders.
 *
 * @template TKeys Headers type.
 * @param {AxiosResponse} response Response.
 * @param {TKeys} keys Header keys.
 *
 * @returns {Record<CamelCase<TKeys>, string | undefined>} Headers.
 */
export function getHeaders<TKeys extends string>({ headers }: AxiosResponse, ...keys: TKeys[]): Record<CamelCase<TKeys>, string | undefined> {
  if (headers.get && typeof headers.get === 'function') {
    return keys.reduce(
      (prev, key) => {
        const headerKey = camelCase(key) as CamelCase<TKeys>;
        const headerValue = (headers.get as NonNullable<typeof headers.get>)(key.toString());

        return {
          ...prev,
          [headerKey]: headerValue,
        };
      },
      {} as Record<CamelCase<TKeys>, string | undefined>,
    );
  }

  throw new ClientError({
    module: 'service',
    message: 'Response headers from service has key override header getter',
    cause: headers,
  });
}

/**
 * Get data from Response.
 *
 * @template TData Data type.
 * @param {AxiosResponse<ServerResponse<TData>>} response Response.
 *
 * @returns {TData} Data.
 */
export function getData<TData>({ data }: AxiosResponse<ServerResponse<TData>>): TData {
  if (data) {
    if ('data' in data) {
      return data.data;
    }

    if ('datas' in data) {
      return data.datas;
    }
  }

  throw new ClientError({
    module: 'service',
    message: 'Response data from service is not available data',
    cause: data,
  });
}

/**
 * Get pageable data from Response.
 *
 * @template TData Data type.
 * @param {QueryRequestData} query Query.
 * @param {AxiosResponse<ServerResponsePageable<TData>>} response Response.
 *
 * @returns {PageableData<TData>} Data.
 */
export function getPageableData<TData extends UnknownArray>(
  { sort = [] }: QueryRequestData,
  { data }: AxiosResponse<ServerResponsePageable<TData>>,
): PageableData<TData> {
  if (data && 'datas' in data && 'pagination' in data) {
    const { datas, pagination } = data;

    const skip = (pagination.pageNumber - 1) * pagination.pageSize;
    return {
      items: orderBy(datas, sort) as TData,
      total: pagination.totalItemCount,
      page: {
        skip,
        take: pagination.pageSize,
        nextSkip: pagination.pageNumber < pagination.pageCount ? skip + pagination.pageSize : undefined,
      },
    };
  }

  throw new ClientError({
    module: 'service',
    message: 'Response data from service is not pageable data',
    cause: data,
  });
}

/**
 * Get nextable data from Response.
 *
 * @template TData Data type.
 * @param {AxiosResponse<TData>} response Response.
 *
 * @returns {NextableData<TData>} Data.
 */
export function getNextableData<TData>(response: AxiosResponse<ServerResponse<TData>>): NextableData<TData> {
  const data = getData(response);
  const { xNextToken } = getHeaders(response, 'X-NextToken');

  return {
    data,
    nextToken: xNextToken,
  };
}

/**
 * Get pageable data with trailing from Response.
 *
 * @template TData Data type.
 * @template TTrailing Trailing type.
 * @param {QueryRequestData} query Query.
 * @param {AxiosResponse<ServerResponsePageableWithTrailing<TData, TTrailing>>} response Response.
 *
 * @returns {PageableDataWithTrailing<TData, TTrailing>} Data.
 */
export function getPageableDataWithTrailing<TData extends UnknownArray, TTrailing>(
  { sort = [] }: QueryRequestData,
  { data }: AxiosResponse<ServerResponsePageableWithTrailing<TData, TTrailing>>,
): PageableDataWithTrailing<TData, TTrailing> {
  if (data && 'data' in data && 'datas' in data && 'pagination' in data) {
    const { datas, pagination } = data;

    const skip = (pagination.pageNumber - 1) * pagination.pageSize;
    return [
      {
        items: orderBy(datas, sort) as TData,
        total: pagination.totalItemCount,
        page: {
          skip,
          take: pagination.pageSize,
          nextSkip: pagination.pageNumber < pagination.pageCount ? skip + pagination.pageSize : undefined,
        },
      },
      data.data,
    ];
  }

  throw new ClientError({
    module: 'service',
    message: 'Response data from service is not pageable data with trailing',
    cause: data,
  });
}
